import Component from '@glimmer/component';
import type { PartnerSpecificField } from '../utils/partner-specific-fields.ts';

interface PartnerFieldWarningSignature {
  Args: {
    /** Custom fields the report references, from `partnerSpecificFields`. */
    fields: PartnerSpecificField[];
    /** Whether the user has accepted missing-field results for partners without these definitions. */
    acknowledged: boolean;
    /** Called with the checkbox's new state. */
    onAcknowledge: (acknowledged: boolean) => void;
  };
  Element: HTMLDivElement;
}

/**
 * Warns that a report shared beyond its own partner uses fields defined per
 * partner, lists them, and asks for an explicit acknowledgement. It never
 * changes the report; the host dialog blocks its action until acknowledged.
 */
export default class PartnerFieldWarning extends Component<PartnerFieldWarningSignature> {
  handleChange = (event: Event) => {
    this.args.onAcknowledge((event.target as HTMLInputElement).checked);
  };

  <template>
    <div
      class="rounded-md border tio-border-warning tio-bg-warning-soft p-3 tio-text-sm-normal"
      data-test-partner-field-warning
      ...attributes
    >
      <p class="tio-text-sm-semibold tio-fg-warning-h">Partner-specific fields</p>
      <p class="mt-1 tio-fg-warning-body">
        These fields are defined per partner and may not exist for every partner you are sharing
        with. Partners without them will see empty columns, and filters on them will match nothing.
      </p>
      <ul class="mt-2 list-inside list-disc tio-fg-warning-body">
        {{#each @fields as |field|}}
          <li data-test-partner-field={{field.key}}>
            {{field.label}}
            <span class="tio-text-xs-normal tio-fg-muted">({{field.key}})</span>
          </li>
        {{/each}}
      </ul>
      <label class="tio-row-top-tight mt-3 tio-fg-warning-body">
        <input
          type="checkbox"
          checked={{@acknowledged}}
          class="mt-1 h-4 w-4 rounded tio-border-default"
          data-test-partner-field-acknowledge
          {{on "change" this.handleChange}}
        />
        <span>I understand some partners may not have these fields.</span>
      </label>
    </div>
  </template>
}
